import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_WhiteBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/white-background',
			name: 'White Background Maker — Change Photo Background to White | Lift',
			description:
				'Make photo background white instantly with AI. No watermark. Upload photo, remove clutter, and create clean white backgrounds for products, profiles, and ads. Try now!',
			url: 'https://liftapp.ai/white-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/white-background#product' },
				{ '@id': 'https://liftapp.ai/white-background#howto' },
				{ '@id': 'https://liftapp.ai/white-background#benefits' },
				{ '@id': 'https://liftapp.ai/white-background#faq' },
				{ '@id': 'https://liftapp.ai/white-background#why-lift' },
				{ '@id': 'https://liftapp.ai/white-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/white-background#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/white-background#product',
			name: 'White Background Maker Online',
			description:
				'Change any photo background to white in seconds. Get a clean, professional white background for product photos, profiles, and resumes, no watermarks.',
			url: 'https://liftapp.ai/white-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/white-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift changes your photo background to pure white automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/white-background#benefits',
			name: 'Who can benefit from White Background Maker?',
			description:
				"Lift's AI White Background Maker is useful for professionals, creators, and businesses who want clean, professional white backgrounds for product photos, profiles, resumes, and marketing materials.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Online stores — Meet marketplace requirements with standardized white backgrounds for product listings. Boost CTR and conversion rates with clean, professional images that make products stand out',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Small business owners — Get professional-looking photos without hiring a photographer or learning Photoshop. Create polished images for websites, social media, and advertising in minutes',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers & SMM managers — Create consistent visuals at scale for ads, landing pages, and campaigns. Speed up creative testing and maintain brand consistency across all channels',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators — Make clean profile photos, thumbnails, and promotional images that keep focus on you or your product. Perfect for YouTube, Instagram, LinkedIn, and portfolio sites',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create polished photos for school IDs, resumes, presentations, and academic portfolios. No design skills required — just upload and get professional results instantly',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Add white background to photo',
				description:
					'Example of how Lift White Background Maker creates clean white backgrounds for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/white-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift White Background Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift White Background Maker is a premium online tool that instantly replaces any photo background with a clean white background with pixel-perfect precision — ideal for product photos, profile pictures, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift White Background Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's white background tool. Our AI automatically detects the subject and changes the background to pure white in seconds — no photo editing experience needed. Download up to 20 edited images with professional white backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift White Background Maker uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/white-background#why-lift',
			name: "Why Lift's AI White Background Maker stands out",
			description:
				"Lift's White Background Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'White background use cases',
				description:
					"Comparison of Lift's AI White Background Maker benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/white-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/white-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'White Background Maker',
					item: 'https://liftapp.ai/white-background',
				},
			],
		},
	],
};
